// ✅ src/components/screens/OrdersScreen.js (FULL - NON-STRICT)
// ✅ Mobile FIX: action buttons (Details + Confirm) now sit at the TOP near buyer name
// ✅ Wraps properly on small screens
// ✅ Details Order -> /seller/orders/:orderId

import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function upper(v) {
  return safe(v).toUpperCase();
}
function moneyGHS(n) {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}
function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
function capStatus(s) {
  const t = safe(s);
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}
function normalizePayLabel(type) {
  const t = upper(type);
  if (t === "MOMO" || t === "MOBILE_MONEY") return "MoMo";
  if (t === "BANK" || t === "BANK_TRANSFER") return "Bank";
  if (t === "CASH") return "Cash";
  return t || "—";
}
function normalizeDelLabel(type) {
  const t = upper(type);
  if (t.includes("PICK")) return "Local Pickup";
  if (t.includes("MEET")) return "Meet-up";
  if (t.includes("DELIV")) return "Delivery";
  return safe(type) || "Delivery";
}

/* ---------------- tracking ---------------- */
const TRACK_STEPS = [
  "PLACED",
  "CONFIRMED",
  "PACKED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "COMPLETED",
];

function statusToStep(status) {
  const s = upper(status);

  if (s === "NEW") return "PLACED";
  if (s === "PENDING_CONFIRMATION") return "PLACED";

  if (s === "PAID") return "CONFIRMED"; // old
  if (s === "CONFIRMED") return "CONFIRMED";

  if (s === "PACKED") return "PACKED";
  if (s === "OUT_FOR_DELIVERY") return "OUT_FOR_DELIVERY";

  if (s === "DELIVERED") return "DELIVERED";
  if (s === "DONE" || s === "COMPLETED") return "COMPLETED";

  return "PLACED";
}

function stepTitle(step) {
  switch (upper(step)) {
    case "PLACED":
      return "Order placed";
    case "CONFIRMED":
      return "Payment confirmed";
    case "PACKED":
      return "Packed";
    case "OUT_FOR_DELIVERY":
      return "Out for delivery";
    case "DELIVERED":
      return "Delivered";
    case "COMPLETED":
      return "Completed";
    default:
      return safe(step) || "—";
  }
}
function prettyStep(step) {
  const s = upper(step);
  if (s === "PLACED") return "Order placed";
  if (s === "CONFIRMED") return "Seller confirmed payment";
  if (s === "PACKED") return "Packed and ready";
  if (s === "OUT_FOR_DELIVERY") return "Out for delivery";
  if (s === "DELIVERED") return "Delivered";
  if (s === "COMPLETED") return "Completed";
  return s;
}
function isCompletedStatus(status, step) {
  const s = upper(status);
  const st = upper(step);
  return s === "DONE" || s === "COMPLETED" || st === "COMPLETED";
}

/* ---------------- component ---------------- */
export default function OrdersScreen({ goBack }) {
  const nav = useNavigate();

  const [user, setUser] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("new"); // new | pending | progress | completed | all

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;

    let alive = true;
    setLoading(true);
    setMsg("");

    // ⚠️ This assumes orders.storeId == seller auth uid.
    // If your orders use sellerId/ownerId/storeDocId, change "storeId" to that field.
    const qy = query(
      collection(db, "orders"),
      where("storeId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      qy,
      (qs) => {
        if (!alive) return;
        setOrders(qs.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        setMsg(err?.message || "Failed to load orders.");
        setLoading(false);
      }
    );

    return () => {
      alive = false;
      try {
        unsub();
      } catch {}
    };
  }, [user]);

  // ✅ NON-STRICT FLAGS (single source of truth)
  const getFlags = (o) => {
    const step = safe(o?.tracking?.currentStep) || statusToStep(o?.status);

    const buyerSaidPaid =
      o?.payment?.paidByBuyer === true ||
      upper(o?.status) === "PENDING_CONFIRMATION" ||
      upper(o?.payment?.status) === "PENDING_CONFIRMATION";

    const sellerConfirmed =
      o?.payment?.sellerConfirmed === true ||
      ["CONFIRMED", "PACKED", "OUT_FOR_DELIVERY", "DELIVERED", "COMPLETED"].includes(
        upper(o?.status)
      );

    const done = isCompletedStatus(o?.status, step);

    return { step, buyerSaidPaid, sellerConfirmed, done };
  };

  // ✅ NON-STRICT FILTERING
  const filtered = useMemo(() => {
    const rows = orders || [];

    if (tab === "new") return rows.filter((o) => !getFlags(o).done);

    if (tab === "pending") {
      return rows.filter((o) => {
        const f = getFlags(o);
        return f.buyerSaidPaid && !f.sellerConfirmed && !f.done;
      });
    }

    if (tab === "progress") {
      return rows.filter((o) => {
        const f = getFlags(o);
        return f.sellerConfirmed && !f.done;
      });
    }

    if (tab === "completed") return rows.filter((o) => getFlags(o).done);

    return rows;
  }, [orders, tab]);

  // ✅ Seller confirms payment
  const confirmPayment = async (o) => {
    if (!user) return;

    try {
      setMsg("");
      const now = Timestamp.now();

      await updateDoc(doc(db, "orders", o.id), {
        confirmedAt: serverTimestamp(),
        confirmedBy: user.uid,
        sellerConfirmNote: "Payment received",

        "payment.sellerConfirmed": true,
        "payment.confirmedAt": serverTimestamp(),
        "payment.confirmedBy": user.uid,

        status: "CONFIRMED",
        updatedAt: serverTimestamp(),

        "tracking.currentStep": "CONFIRMED",
        "tracking.history": arrayUnion({
          step: "CONFIRMED",
          text: "Seller confirmed payment",
          at: now,
        }),
      });

      await addDoc(collection(db, "stores", user.uid, "notifications"), {
        type: "PAYMENT_CONFIRMED",
        title: "Payment confirmed",
        body: `You confirmed payment for order ${safe(o.orderNumber) || o.id}.`,
        orderId: o.id,
        createdAt: serverTimestamp(),
        isRead: false,
      });

      setMsg("✅ Payment confirmed.");
      setTimeout(() => setMsg(""), 1500);
    } catch (e) {
      setMsg(e?.message || "Failed to confirm payment.");
    }
  };

  // ✅ Seller sets delivery progress (tracking) - blocks backwards only
  const setTrackingStep = async (o, step) => {
    if (!user) return;

    const upperStep = upper(step);
    if (!TRACK_STEPS.includes(upperStep)) return;

    const current = safe(o?.tracking?.currentStep) || statusToStep(o?.status);
    const curIdx = Math.max(0, TRACK_STEPS.indexOf(upper(current)));
    const nextIdx = TRACK_STEPS.indexOf(upperStep);

    if (nextIdx < curIdx) {
      setMsg("You can’t move tracking backwards.");
      setTimeout(() => setMsg(""), 1500);
      return;
    }

    let status = upper(o?.status) || "NEW";
    if (upperStep === "CONFIRMED") status = "CONFIRMED";
    if (upperStep === "PACKED") status = "PACKED";
    if (upperStep === "OUT_FOR_DELIVERY") status = "OUT_FOR_DELIVERY";
    if (upperStep === "DELIVERED") status = "DELIVERED";
    if (upperStep === "COMPLETED") status = "COMPLETED";

    try {
      setMsg("");
      const now = Timestamp.now();

      await updateDoc(doc(db, "orders", o.id), {
        status,
        updatedAt: serverTimestamp(),
        "tracking.currentStep": upperStep,
        "tracking.history": arrayUnion({
          step: upperStep,
          text: prettyStep(upperStep),
          at: now,
        }),
      });

      setMsg(`✅ Set to: ${stepTitle(upperStep)}`);
      setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      setMsg(e?.message || "Failed to update tracking.");
    }
  };

  const openProof = (url) => {
    const u = safe(url);
    if (!u) return;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  const goDetails = (orderId) => {
    nav(`/seller/orders/${orderId}`);
  };

  return (
    <div className="os-page">
      <style>{css}</style>

      <div className="os-wrap">
        <div className="os-top">
          <button className="os-back" onClick={goBack}>
            ← Back
          </button>
          <div className="os-title">Orders</div>
          <div style={{ width: 70 }} />
        </div>

        {msg ? (
          <div className={`os-msg ${msg.includes("✅") ? "" : "is-err"}`}>
            {msg}
          </div>
        ) : null}

        {/* Tabs */}
        <div className="os-tabs">
          <button
            className={`os-tab ${tab === "new" ? "is-active" : ""}`}
            onClick={() => setTab("new")}
          >
            New
          </button>
          <button
            className={`os-tab ${tab === "pending" ? "is-active" : ""}`}
            onClick={() => setTab("pending")}
          >
            Pending
          </button>
          <button
            className={`os-tab ${tab === "progress" ? "is-active" : ""}`}
            onClick={() => setTab("progress")}
          >
            In Progress
          </button>
          <button
            className={`os-tab ${tab === "completed" ? "is-active" : ""}`}
            onClick={() => setTab("completed")}
          >
            Completed
          </button>
          <button
            className={`os-tab ${tab === "all" ? "is-active" : ""}`}
            onClick={() => setTab("all")}
          >
            All
          </button>
        </div>

        {/* List */}
        <div className="os-card">
          {loading ? (
            <div>Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="os-empty">No orders.</div>
          ) : (
            filtered.map((o) => {
              const buyerName = safe(o?.buyer?.name) || "Customer";
              const buyerPhone = safe(o?.buyer?.phone) || "—";

              const payType = normalizePayLabel(o?.payment?.type) || "—";
              const payLabel = safe(o?.payment?.label) || payType;
              const payRef = safe(o?.payment?.reference);
              const proofUrl = safe(o?.payment?.proofUrl);

              const delType = normalizeDelLabel(o?.delivery?.type || "DELIVERY");
              const total = moneyGHS(o?.total);
              const status = capStatus(o?.status);

              const f = getFlags(o);
              const currentStep = f.step;
              const curIdx = Math.max(0, TRACK_STEPS.indexOf(upper(currentStep)));
              const nextStep = TRACK_STEPS[Math.min(TRACK_STEPS.length - 1, curIdx + 1)];
              const done = f.done;

              return (
                <div className="os-row" key={o.id}>
                  {/* ✅ HEADER: buyer + buttons at TOP */}
                  <div className="os-head">
                    <div className="os-rowtitle">
                      {buyerName} <span className="os-muted">• {buyerPhone}</span>
                    </div>

                    <div className="os-actions">
                      <button className="os-soft os-softSmall" onClick={() => goDetails(o.id)}>
                        Details
                      </button>

                      {!f.sellerConfirmed ? (
                        <button className="os-primary os-primarySmall" onClick={() => confirmPayment(o)}>
                          Confirm
                        </button>
                      ) : (
                        <button className="os-soft os-softSmall" disabled>
                          Confirmed
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="os-sub">
                    {total} • {payLabel} • {delType} • {fmtDate(o?.createdAt)}
                  </div>

                  {/* payment details */}
                  <div className="os-payline">
                    <div className="os-paychip">
                      <b>Ref:</b> {payRef || "—"}
                    </div>
                    <div className="os-paychip">
                      <b>Proof:</b>{" "}
                      {proofUrl ? (
                        <button className="os-proofbtn" type="button" onClick={() => openProof(proofUrl)}>
                          View
                        </button>
                      ) : (
                        "—"
                      )}
                    </div>

                    {proofUrl ? (
                      <button
                        className="os-proofthumb"
                        type="button"
                        onClick={() => openProof(proofUrl)}
                        title="Open proof"
                      >
                        <img src={proofUrl} alt="proof" />
                      </button>
                    ) : null}
                  </div>

                  <div className="os-badges">
                    <span className="os-badge">{status}</span>

                    {f.buyerSaidPaid ? (
                      <span className="os-badge warn">Buyer submitted payment</span>
                    ) : (
                      <span className="os-badge">Not paid yet</span>
                    )}

                    {f.sellerConfirmed ? (
                      <span className="os-badge ok">Seller confirmed</span>
                    ) : (
                      <span className="os-badge warn">Seller not confirmed</span>
                    )}

                    <span className="os-badge">Tracking: {stepTitle(currentStep)}</span>
                    {done ? <span className="os-badge ok">Completed</span> : null}
                  </div>

                  {/* Tracking controls */}
                  <div className="os-track">
                    {TRACK_STEPS.map((s, idx) => {
                      const on = idx <= curIdx;
                      const disabled = done && s !== "COMPLETED";

                      return (
                        <button
                          key={s}
                          className={`os-chip ${on ? "on" : ""}`}
                          type="button"
                          onClick={() => setTrackingStep(o, s)}
                          disabled={disabled}
                          title={`Set: ${stepTitle(s)}`}
                        >
                          {stepTitle(s)}
                        </button>
                      );
                    })}

                    <button
                      className="os-next"
                      type="button"
                      onClick={() => setTrackingStep(o, nextStep)}
                      disabled={done}
                      title={done ? "Already completed" : "Next step"}
                    >
                      Next →
                    </button>
                  </div>

                  {/* Hint */}
                  <div className="os-hintLine">
                    {!f.buyerSaidPaid ? (
                      <span>Waiting for buyer to click “I’ve Paid”.</span>
                    ) : !f.sellerConfirmed ? (
                      <span>Buyer paid — confirm it.</span>
                    ) : done ? (
                      <span>Order completed ✅</span>
                    ) : (
                      <span>Update delivery steps.</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="os-note">
          🔥 Flow:
          <br />• Buyer clicks <b>I’ve Paid</b> → order becomes <b>PENDING_CONFIRMATION</b> (and proof/ref saved)
          <br />• Seller clicks <b>Confirm</b> → order becomes <b>CONFIRMED</b>
          <br />• Seller updates tracking: <b>Packed</b> → <b>Out for delivery</b> → <b>Delivered</b> → <b>Completed</b>
        </div>
      </div>
    </div>
  );
}

/* ---------------- CSS ---------------- */
const css = `
.os-page{min-height:100vh;background:#f3f6ff;padding:16px;font-family:Inter,system-ui,Segoe UI,Arial;color:#0f172a;}
.os-wrap{max-width:980px;margin:0 auto;display:grid;gap:12px;}
.os-top{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.os-back{border:1px solid #e7ecf6;background:#fff;border-radius:12px;padding:10px 12px;font-weight:900;cursor:pointer;}
.os-title{font-weight:1100;font-size:20px;}
.os-msg{border:1px solid #e7ecf6;background:#fff;border-radius:14px;padding:12px;font-weight:900;}
.os-msg.is-err{border-color:rgba(255,70,120,0.35);background:rgba(255,70,120,0.06);color:#b91c1c;}

.os-tabs{display:flex;gap:10px;background:#f7f9ff;border:1px solid #eef2ff;border-radius:14px;padding:8px;width:fit-content;flex-wrap:wrap;}
.os-tab{border:0;background:transparent;padding:8px 12px;border-radius:12px;cursor:pointer;font-weight:950;color:#64748b;}
.os-tab.is-active{background:#2f61c5;color:#fff;box-shadow:0 10px 24px rgba(47,97,197,0.18);}

.os-card{background:#fff;border:1px solid #e7ecf6;border-radius:18px;padding:12px;box-shadow:0 10px 26px rgba(15,23,42,0.06);display:grid;gap:10px;}
.os-row{border:1px solid #eef2ff;background:#fbfdff;border-radius:14px;padding:12px;display:grid;gap:10px;}

.os-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;}
.os-rowtitle{font-weight:1100;min-width:0;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.os-muted{color:#94a3b8;font-weight:900;}

.os-actions{display:flex;gap:8px;flex:0 0 auto;flex-wrap:wrap;justify-content:flex-end;}
.os-primary{border:0;background:#2f61c5;color:#fff;border-radius:12px;padding:10px 12px;font-weight:1000;cursor:pointer;box-shadow:0 10px 22px rgba(47,97,197,0.18);}
.os-soft{border:1px solid #e7ecf6;background:#f7fbff;border-radius:12px;padding:10px 12px;font-weight:1000;cursor:pointer;}
.os-primary:disabled,.os-soft:disabled{opacity:.55;cursor:not-allowed;}

.os-primarySmall{padding:8px 10px;border-radius:10px;font-size:12px;}
.os-softSmall{padding:8px 10px;border-radius:10px;font-size:12px;}

.os-sub{color:#64748b;font-weight:900;font-size:12px;}

.os-payline{display:flex;gap:10px;align-items:center;flex-wrap:wrap;}
.os-paychip{border:1px solid #e7ecf6;background:#fff;border-radius:999px;padding:6px 10px;font-weight:950;font-size:12px;color:#334155;display:flex;gap:6px;align-items:center;}
.os-proofbtn{border:0;background:#0f172a;color:#fff;border-radius:999px;padding:6px 10px;font-weight:1000;cursor:pointer;}
.os-proofthumb{border:1px solid #e7ecf6;background:#fff;border-radius:12px;padding:0;cursor:pointer;overflow:hidden;width:44px;height:44px;}
.os-proofthumb img{width:100%;height:100%;object-fit:cover;display:block;}

.os-badges{display:flex;gap:8px;flex-wrap:wrap;}
.os-badge{display:inline-flex;align-items:center;padding:6px 10px;border-radius:999px;border:1px solid #e7ecf6;background:#f7fbff;font-weight:950;font-size:12px;color:#334155;}
.os-badge.ok{background:#f0fdf4;border-color:#bbf7d0;color:#166534;}
.os-badge.warn{background:#fff7ed;border-color:#fed7aa;color:#9a3412;}

.os-track{display:flex;gap:8px;flex-wrap:wrap;align-items:center;}
.os-chip{border:1px solid #e7ecf6;background:#fff;border-radius:999px;padding:8px 10px;font-weight:1000;font-size:12px;cursor:pointer;}
.os-chip.on{background:#eaf2ff;border-color:#cfe3ff;color:#1d4ed8;}
.os-chip:disabled{opacity:.55;cursor:not-allowed;}
.os-next{border:0;background:#0f172a;color:#fff;padding:8px 12px;border-radius:12px;font-weight:1000;cursor:pointer;}
.os-next:disabled{opacity:.55;cursor:not-allowed;}

.os-hintLine{color:#64748b;font-weight:900;font-size:12px;}

.os-empty{color:#64748b;font-weight:900;}
.os-note{color:#64748b;font-weight:900;font-size:12px;line-height:1.6;}

@media(max-width:520px){
  .os-tabs{width:100%;}
  .os-tab{flex:1;text-align:center;}

  /* ✅ make header wrap nicely on mobile */
  .os-head{flex-direction:column;align-items:stretch;}
  .os-rowtitle{white-space:normal;overflow:visible;text-overflow:unset;}
  .os-actions{justify-content:flex-start;}
  .os-actions button{flex:1;} /* details + confirm become equal width */
}
`;
